import React, { useCallback, useContext, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import AppContext from '../../Context/AppContext';
import { invalidateElearningCache } from '../DashboardV2/elearningCache';
import {
    enablePortalV2Session,
    resolveUserStepPathV2,
} from '../../util/portalV2';
import { getExigoUserIdFromSession, isV2Admin } from '../../util/v2Admin';
import { fetchTestLabUserInfo, resetUserAccount } from './testLabApi';
import './DevTestLab.css';

const STEP_LABELS = {
    0: 'Customize',
    1: 'Step 1 — Assessment',
    2: 'Step 2 — Goals',
    3: 'Step 3 — Actions',
    4: 'Dashboard',
    5: 'Mid-year review',
    6: 'Year in review',
    7: 'Revise goals',
};

const RESET_PRESETS = [
    {
        stepId: 0,
        title: 'Full reset → Customize',
        description:
            'Wipes assessment, goals, actions, achievements, and e-learning. Starts from customize.',
    },
    {
        stepId: 1,
        title: 'Reset → Step 1',
        description:
            'Clears goals, actions, achievements, and e-learning. Keeps assessment shell.',
    },
    {
        stepId: 2,
        title: 'Reset → Step 2',
        description: 'Clears goals onward. Good for re-testing goal selection.',
    },
    {
        stepId: 3,
        title: 'Reset → Step 3',
        description: 'Clears actions onward. Good for re-testing daily actions.',
    },
    {
        stepId: 4,
        title: 'Reset → Dashboard (no e-learning)',
        description:
            'Clears achievements and e-learning modules. Lands on dashboard with a clean learning path.',
    },
];

function formatStep(stepId) {
    return STEP_LABELS[stepId] ?? `Step ${stepId}`;
}

function pick(obj, ...keys) {
    if (!obj) return undefined;
    for (const key of keys) {
        if (obj[key] != null && obj[key] !== '') return obj[key];
    }
    return undefined;
}

/**
 * Admin-only account reset console.
 * Search any Exigo Customer Id → show info → confirm → reset.
 */
const DevTestLab = () => {
    const { login, checkUserSession, logOut, updateUserStepId } =
        useContext(AppContext);

    const session = checkUserSession();
    const adminExigoId = getExigoUserIdFromSession(session);
    const isAdmin =
        Boolean(session?.token) && isV2Admin(adminExigoId);

    const [loginId, setLoginId] = useState(
        String(adminExigoId || '19113609')
    );
    const [password, setPassword] = useState('');
    const [loginBusy, setLoginBusy] = useState(false);

    const [searchId, setSearchId] = useState('');
    const [searchBusy, setSearchBusy] = useState(false);
    const [target, setTarget] = useState(null);
    const [targetExigoId, setTargetExigoId] = useState(null);

    const [resetBusy, setResetBusy] = useState(false);
    const [pendingReset, setPendingReset] = useState(null);
    const [error, setError] = useState('');
    const [notice, setNotice] = useState('');
    const [lastReset, setLastReset] = useState(null);

    const currentStep = pick(target, 'currentStep', 'CurrentStep');
    const dbName = pick(target, 'name', 'Name');
    const oolaUserId = pick(target, 'id', 'Id');

    const canResetToStep = useCallback(
        (stepId) => {
            if (currentStep == null) return true;
            if (stepId === 5 || stepId === 6 || stepId === 7) return true;
            return Number(currentStep) >= stepId;
        },
        [currentStep]
    );

    const handleAdminLogin = async (event) => {
        event.preventDefault();
        setError('');
        setNotice('');
        const id = Number(String(loginId || '').replace(/\D/g, ''));
        if (!isV2Admin(id)) {
            setError(
                'Only V2 admin Customer Ids can open this console (19113609 / 3635).'
            );
            return;
        }
        setLoginBusy(true);
        try {
            const result = await login(String(id), password || '', 2);
            if (typeof result === 'number') {
                enablePortalV2Session();
                setNotice('Signed in as admin. Search any Customer Id to reset.');
                setPassword('');
                return;
            }
            const msg =
                result?.data?.message ||
                result?.data?.Message ||
                'Login failed. Use AuthExceptions password (OolaV2Admin) or USANA credentials.';
            setError(msg);
        } catch (err) {
            setError(err?.message || 'Login failed.');
        } finally {
            setLoginBusy(false);
        }
    };

    const handleSearch = async (event) => {
        event?.preventDefault?.();
        setError('');
        setNotice('');
        setTarget(null);
        setLastReset(null);

        const id = Number(String(searchId || '').replace(/\D/g, ''));
        if (!Number.isFinite(id) || id <= 0) {
            setError('Enter a valid Exigo Customer Id.');
            return;
        }
        if (!session?.token) {
            setError('Admin session required.');
            return;
        }

        setSearchBusy(true);
        const { error: reqError, data, status } = await fetchTestLabUserInfo(
            id,
            session.token
        );
        setSearchBusy(false);

        if (reqError) {
            const msg =
                typeof reqError.response?.data === 'string'
                    ? reqError.response.data
                    : reqError.response?.data?.message ||
                      reqError.response?.data?.errorMessage ||
                      reqError.message ||
                      'Account not found.';
            setError(msg);
            if (status === 401) logOut?.();
            return;
        }

        setTargetExigoId(id);
        setTarget(data);
        setNotice(`Account found for Customer Id ${id}.`);
    };

    const requestReset = (stepId) => {
        if (!target || !targetExigoId || !dbName) {
            setError('Search and load an account before resetting.');
            return;
        }
        if (!canResetToStep(stepId)) {
            setError(
                `Cannot reset to step ${stepId} while account is on step ${currentStep}.`
            );
            return;
        }
        setPendingReset({
            stepId,
            label: formatStep(stepId),
            preset: RESET_PRESETS.find((p) => p.stepId === stepId),
        });
        setError('');
    };

    const cancelReset = () => {
        if (resetBusy) return;
        setPendingReset(null);
    };

    const confirmReset = async () => {
        if (!pendingReset || !target || !targetExigoId || !dbName) return;

        const { stepId, label } = pendingReset;
        setResetBusy(true);
        setError('');
        setNotice('');

        const { error: reqError, data } = await resetUserAccount({
            exigoUserId: targetExigoId,
            name: dbName,
            stepId,
            token: session.token,
        });

        setResetBusy(false);

        if (reqError) {
            const msg =
                typeof reqError.response?.data === 'string'
                    ? reqError.response.data
                    : reqError.response?.data?.message ||
                      reqError.response?.data?.errorMessage ||
                      reqError.message ||
                      'Reset failed.';
            setError(msg);
            setPendingReset(null);
            return;
        }

        if (oolaUserId && Number(oolaUserId) === Number(session.userId)) {
            invalidateElearningCache(session.userId);
            await updateUserStepId?.();
        }

        const refreshed = await fetchTestLabUserInfo(
            targetExigoId,
            session.token
        );
        if (!refreshed.error && refreshed.data) {
            setTarget(refreshed.data);
        }

        setLastReset({ stepId, at: new Date(), response: data });
        setPendingReset(null);
        setNotice(
            `Account ${targetExigoId} (${dbName}) reset to ${label}.`
        );
    };

    const goPath = useMemo(() => {
        const step =
            lastReset?.response?.currentStep ??
            currentStep ??
            session?.userStepId;
        return resolveUserStepPathV2(step);
    }, [lastReset, currentStep, session?.userStepId]);

    if (!isAdmin) {
        return (
            <div className="dev-test-lab">
                <div className="dev-test-lab__shell">
                    <span className="dev-test-lab__badge">V2 Admin</span>
                    <h1 className="dev-test-lab__title">Account reset console</h1>
                    <p className="dev-test-lab__lead">
                        Sign in with your admin Customer Id to search and reset
                        any account by Exigo Id. Does not affect V1 menus.
                    </p>

                    <form
                        className="dev-test-lab__card"
                        onSubmit={handleAdminLogin}
                    >
                        <h2>Admin login</h2>
                        <div className="dev-test-lab__field">
                            <label htmlFor="dev-reset-admin-id">
                                Admin Customer Id
                            </label>
                            <input
                                id="dev-reset-admin-id"
                                type="text"
                                inputMode="numeric"
                                value={loginId}
                                onChange={(e) => setLoginId(e.target.value)}
                                placeholder="19113609 or 3635"
                                autoComplete="username"
                            />
                        </div>
                        <div className="dev-test-lab__field">
                            <label htmlFor="dev-reset-admin-pass">
                                Password
                            </label>
                            <input
                                id="dev-reset-admin-pass"
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="OolaV2Admin"
                                autoComplete="current-password"
                            />
                        </div>
                        {error ? (
                            <p className="dev-test-lab__error" role="alert">
                                {error}
                            </p>
                        ) : null}
                        <button
                            type="submit"
                            className="dev-test-lab__btn dev-test-lab__btn--gold"
                            disabled={loginBusy}
                        >
                            {loginBusy ? 'Signing in…' : 'Enter console'}
                        </button>
                    </form>
                </div>
            </div>
        );
    }

    return (
        <div className="dev-test-lab">
            <div className="dev-test-lab__shell">
                <span className="dev-test-lab__badge">V2 Admin</span>
                <h1 className="dev-test-lab__title">Account reset console</h1>
                <p className="dev-test-lab__lead">
                    Search any Exigo Customer Id, review account info, then
                    confirm before resetting. Signed in as admin{' '}
                    <strong>{adminExigoId}</strong>.
                </p>

                <form className="dev-test-lab__card" onSubmit={handleSearch}>
                    <h2>Find account</h2>
                    <div className="dev-test-lab__field">
                        <label htmlFor="dev-reset-search">
                            Exigo Customer Id
                        </label>
                        <div className="dev-test-lab__search-row">
                            <input
                                id="dev-reset-search"
                                type="text"
                                inputMode="numeric"
                                value={searchId}
                                onChange={(e) => setSearchId(e.target.value)}
                                placeholder="e.g. 14832"
                                autoComplete="off"
                            />
                            <button
                                type="submit"
                                className="dev-test-lab__btn dev-test-lab__btn--gold"
                                disabled={searchBusy || resetBusy}
                            >
                                {searchBusy ? 'Searching…' : 'Search'}
                            </button>
                        </div>
                    </div>
                    <p className="dev-test-lab__hint">
                        Looks up <code>Account/GetUserInfo/&#123;exigoUserId&#125;</code>.
                        Reset uses <code>PUT Account/ResetUserAccount</code>.
                    </p>
                </form>

                {target ? (
                    <>
                        <div className="dev-test-lab__card">
                            <h2>Account found</h2>
                            <div className="dev-test-lab__status-grid">
                                <div className="dev-test-lab__stat">
                                    <span className="dev-test-lab__stat-label">
                                        Customer Id
                                    </span>
                                    <span className="dev-test-lab__stat-value">
                                        {targetExigoId}
                                    </span>
                                </div>
                                <div className="dev-test-lab__stat">
                                    <span className="dev-test-lab__stat-label">
                                        Oola User Id
                                    </span>
                                    <span className="dev-test-lab__stat-value">
                                        {oolaUserId ?? '—'}
                                    </span>
                                </div>
                                <div className="dev-test-lab__stat">
                                    <span className="dev-test-lab__stat-label">
                                        DB Name
                                    </span>
                                    <span className="dev-test-lab__stat-value">
                                        {dbName || '—'}
                                    </span>
                                </div>
                                <div className="dev-test-lab__stat">
                                    <span className="dev-test-lab__stat-label">
                                        Current step
                                    </span>
                                    <span className="dev-test-lab__stat-value">
                                        {formatStep(currentStep)} (
                                        {currentStep})
                                    </span>
                                </div>
                                <div className="dev-test-lab__stat">
                                    <span className="dev-test-lab__stat-label">
                                        Goals
                                    </span>
                                    <span className="dev-test-lab__stat-value">
                                        {pick(target, 'goals', 'Goals') || '—'}
                                    </span>
                                </div>
                                <div className="dev-test-lab__stat">
                                    <span className="dev-test-lab__stat-label">
                                        Actions
                                    </span>
                                    <span className="dev-test-lab__stat-value">
                                        {pick(target, 'actions', 'Actions') ||
                                            '—'}
                                    </span>
                                </div>
                                <div className="dev-test-lab__stat">
                                    <span className="dev-test-lab__stat-label">
                                        E-learning
                                    </span>
                                    <span className="dev-test-lab__stat-value">
                                        {pick(
                                            target,
                                            'currentElearningModule',
                                            'CurrentElearningModule'
                                        ) || 'None / not started'}
                                    </span>
                                </div>
                            </div>
                            <div className="dev-test-lab__btn-row">
                                <button
                                    type="button"
                                    className="dev-test-lab__btn"
                                    onClick={handleSearch}
                                    disabled={searchBusy || resetBusy}
                                >
                                    Refresh
                                </button>
                                <button
                                    type="button"
                                    className="dev-test-lab__btn"
                                    onClick={() => {
                                        setTarget(null);
                                        setTargetExigoId(null);
                                        setLastReset(null);
                                    }}
                                    disabled={resetBusy}
                                >
                                    Clear
                                </button>
                            </div>
                        </div>

                        <div className="dev-test-lab__card">
                            <h2>Reset account</h2>
                            <p className="dev-test-lab__hint">
                                You can only reset to a step at or before the
                                account&apos;s current progress (API rule). A
                                confirmation popup is required before each
                                reset.
                            </p>
                            <div className="dev-test-lab__presets">
                                {RESET_PRESETS.map((preset) => {
                                    const enabled = canResetToStep(
                                        preset.stepId
                                    );
                                    return (
                                        <button
                                            key={preset.stepId}
                                            type="button"
                                            className="dev-test-lab__preset"
                                            disabled={
                                                resetBusy ||
                                                !enabled ||
                                                !dbName
                                            }
                                            onClick={() =>
                                                requestReset(preset.stepId)
                                            }
                                            title={
                                                enabled
                                                    ? undefined
                                                    : `Not available while on step ${currentStep}`
                                            }
                                        >
                                            <span className="dev-test-lab__preset-title">
                                                {preset.title}
                                            </span>
                                            <span className="dev-test-lab__preset-desc">
                                                {preset.description}
                                            </span>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    </>
                ) : null}

                <div className="dev-test-lab__card">
                    <h2>Continue testing (V2)</h2>
                    <div className="dev-test-lab__links">
                        <Link to="/dashboard-v2?lab=1">← Back to Lab</Link>
                        <Link to={goPath}>Open current step →</Link>
                        <Link to="/dashboard-v2">Dashboard</Link>
                        <Link to="/dashboard-v2?elearning=1">E-learning hub</Link>
                    </div>
                    <div className="dev-test-lab__btn-row">
                        <button
                            type="button"
                            className="dev-test-lab__btn dev-test-lab__btn--danger"
                            onClick={() => logOut?.()}
                        >
                            Sign out
                        </button>
                    </div>
                </div>

                {notice ? (
                    <p className="dev-test-lab__success" role="status">
                        {notice}
                    </p>
                ) : null}
                {error ? (
                    <p className="dev-test-lab__error" role="alert">
                        {error}
                    </p>
                ) : null}
            </div>

            {pendingReset ? (
                <div
                    className="dev-test-lab__modal-backdrop"
                    role="presentation"
                    onClick={cancelReset}
                >
                    <div
                        className="dev-test-lab__modal"
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="dev-reset-confirm-title"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <h2 id="dev-reset-confirm-title">
                            Confirm account reset
                        </h2>
                        <p>
                            This will permanently rewind progress for the
                            selected customer. This cannot be undone from the
                            UI.
                        </p>
                        <ul className="dev-test-lab__modal-list">
                            <li>
                                <strong>Customer Id:</strong> {targetExigoId}
                            </li>
                            <li>
                                <strong>Name:</strong> {dbName}
                            </li>
                            <li>
                                <strong>Current step:</strong>{' '}
                                {formatStep(currentStep)} ({currentStep})
                            </li>
                            <li>
                                <strong>Reset to:</strong> {pendingReset.label}{' '}
                                (step {pendingReset.stepId})
                            </li>
                        </ul>
                        {pendingReset.preset ? (
                            <p className="dev-test-lab__hint">
                                {pendingReset.preset.description}
                            </p>
                        ) : null}
                        <div className="dev-test-lab__btn-row">
                            <button
                                type="button"
                                className="dev-test-lab__btn"
                                onClick={cancelReset}
                                disabled={resetBusy}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="dev-test-lab__btn dev-test-lab__btn--danger-solid"
                                onClick={confirmReset}
                                disabled={resetBusy}
                            >
                                {resetBusy
                                    ? 'Resetting…'
                                    : `Yes, reset to ${pendingReset.label}`}
                            </button>
                        </div>
                    </div>
                </div>
            ) : null}
        </div>
    );
};

export default DevTestLab;
